import { useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Input, Text } from '@kula-ai/components';
// The app's standard select (react-select + design-system styles) — the same
// component family the ATS job-info / hiring-team screens use, so pickers in
// chat look and behave like pickers everywhere else (search, multi, chips).
import { KSelect } from '@/ui-library/components/molecules/KSelect';

// Above this many options a single-select renders as a searchable dropdown
// (like Kula's department/office selects) instead of a wrapped row of chips.
const DROPDOWN_THRESHOLD = 5;
// Server-search debounce + minimum term length. Below the minimum the picker
// shows the embedded first page again.
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;
import { searchLookupOptions, type LookupOption } from '../../../api/lookup';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { ClarifyFormBlockData, ClarifyFormField } from '../../../types';

interface ClarifyFormBlockProps {
  data: ClarifyFormBlockData;
}

type FieldValue = string | string[];

// Initial value per field: a multi picker starts with the pre-selected option
// (or empty); a single picker defaults to its pre-selected option (the
// agent's best guess) or the first option; a text field to its prefill.
const initialValue = (field: ClarifyFormField): FieldValue => {
  if (field.type === 'picker') {
    if (field.multi) return field.selected ? [field.selected] : [];
    return field.selected || field.options?.[0]?.value || '';
  }
  return field.prefill;
};

export const ClarifyFormBlock = ({ data }: ClarifyFormBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  const disabled = isSending || isStreaming;

  const [values, setValues] = useState<Record<string, FieldValue>>(() =>
    Object.fromEntries(data.actions.map((a) => [a.key, initialValue(a)]))
  );

  // On-the-fly search state for unbounded pickers (fields with `search`).
  // `remote[key]` non-null = show server results instead of the embedded
  // first page. `lookupLabels` caches value→label for EVERY option ever
  // fetched, so a selection made from a search result still renders its
  // label (in multi chips and in the submit substitution) after the search
  // results themselves are replaced by a newer query.
  const [remote, setRemote] = useState<Record<string, LookupOption[] | null>>(
    {}
  );
  const [searching, setSearching] = useState<Record<string, boolean>>({});
  const [lookupLabels, setLookupLabels] = useState<Record<string, string>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const abortRef = useRef<Record<string, AbortController>>({});

  const queueLookup = (field: ClarifyFormField, term: string) => {
    const search = field.search;
    if (!search) return;
    const key = field.key;
    clearTimeout(timersRef.current[key]);
    const trimmed = term.trim();
    if (trimmed.length < SEARCH_MIN_CHARS) {
      abortRef.current[key]?.abort();
      setRemote((p) => ({ ...p, [key]: null }));
      setSearching((p) => ({ ...p, [key]: false }));
      return;
    }
    setSearching((p) => ({ ...p, [key]: true }));
    timersRef.current[key] = setTimeout(() => {
      abortRef.current[key]?.abort();
      const ctrl = new AbortController();
      abortRef.current[key] = ctrl;
      searchLookupOptions(search, trimmed, ctrl.signal)
        .then((opts) => {
          if (ctrl.signal.aborted) return;
          setRemote((p) => ({ ...p, [key]: opts }));
          setLookupLabels((p) => {
            const next = { ...p };
            for (const o of opts) next[o.value] = o.label;
            return next;
          });
          setSearching((p) => ({ ...p, [key]: false }));
        })
        .catch(() => {
          // Search failing degrades to the embedded first page — never
          // block the form on a lookup hiccup.
          if (ctrl.signal.aborted) return;
          setSearching((p) => ({ ...p, [key]: false }));
        });
    }, SEARCH_DEBOUNCE_MS);
  };

  // Label for an option value: embedded options first, then anything a
  // server search ever returned, then the raw value as a last resort.
  const optionLabel = (field: ClarifyFormField, value: string): string =>
    field.options?.find((o) => o.value === value)?.label ??
    lookupLabels[value] ??
    value;

  // Human-readable substitution for the {key} placeholder: pickers show their
  // label(s) ("Engineering (R&D)", "Jane Smith, Raj Patel"), text fields the
  // raw value. The machine values/IDs the agent needs travel separately in a
  // <ctx:…> marker (see handleSubmit) — kept out of the visible text so it
  // reads cleanly.
  const substitution = (field: ClarifyFormField, value: FieldValue): string => {
    if (Array.isArray(value)) {
      if (value.length === 0) return 'none';
      return value.map((v) => optionLabel(field, v)).join(', ');
    }
    if (field.type === 'picker') return optionLabel(field, value);
    return value;
  };

  const setValue = (key: string, value: FieldValue) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    if (disabled) return;
    // Human-readable instruction (labels), for both the agent and the chat
    // bubble the user sees.
    const readable = data.actions.reduce(
      (tpl, field) =>
        tpl.replace(
          new RegExp(`\\{${field.key}\\}`, 'g'),
          substitution(field, values[field.key] ?? '')
        ),
      data.warning
    );
    // Exact machine values/IDs (dept:261, office:99, full_time, user:4,user:9)
    // for the agent, in a ctx marker the user bubble strips from display.
    // Multi values join with commas within the field.
    const ctx = data.actions
      .map((f) => {
        const v = values[f.key] ?? '';
        return `${f.key}=${Array.isArray(v) ? v.join(',') : v}`;
      })
      .join('; ');
    void send(`${readable} <ctx:fields=${ctx}>`);
  };

  // Standard-select adapters: KSelect (react-select) works with option objects.
  const toOption = (field: ClarifyFormField, value: string) => ({
    value,
    label: optionLabel(field, value),
  });

  const renderPicker = (field: ClarifyFormField) => {
    const hasSearch = !!field.search;
    const remoteOpts = hasSearch ? remote[field.key] : null;
    const opts = (remoteOpts ?? field.options ?? []).map((o) => ({
      value: o.value,
      label: o.label,
    }));

    // Extra props for server-searching pickers: debounce the typed term to
    // /lookup, show the spinner, and — when server results are live — turn
    // off react-select's own filtering (the server already matched; its
    // labels may not contain the literal term).
    const searchProps = hasSearch
      ? {
          onInputChange: (v: string, meta: { action: string }) => {
            if (meta.action === 'input-change') queueLookup(field, v);
          },
          isLoading: !!searching[field.key],
          filterOption: remoteOpts ? () => true : undefined,
          noOptionsMessage: ({ inputValue }: { inputValue: string }) =>
            inputValue.trim().length < SEARCH_MIN_CHARS
              ? 'Type to search…'
              : 'No matches',
        }
      : {};

    if (field.multi) {
      const current = (values[field.key] as string[]) ?? [];
      return (
        <KSelect
          isMulti
          options={opts}
          value={current.map((v) => toOption(field, v))}
          onChange={(sel: { value: string }[] | null) =>
            setValue(
              field.key,
              (sel ?? []).map((o) => o.value)
            )
          }
          isDisabled={disabled}
          placeholder={
            hasSearch
              ? `Search ${field.label.toLowerCase()}`
              : `Select ${field.label.toLowerCase()}`
          }
          menuPortalTarget={document.body}
          {...searchProps}
        />
      );
    }

    // Server-searching pickers are ALWAYS a dropdown — their embedded
    // options are just a first page of an unbounded list, so chips would
    // misrepresent the choice space.
    if (hasSearch || opts.length > DROPDOWN_THRESHOLD) {
      const current = (values[field.key] as string) ?? '';
      return (
        <KSelect
          options={opts}
          value={current ? toOption(field, current) : null}
          onChange={(sel: { value: string } | null) =>
            setValue(field.key, sel?.value ?? '')
          }
          isDisabled={disabled}
          placeholder={
            hasSearch
              ? `Search ${field.label.toLowerCase()}`
              : `Select ${field.label.toLowerCase()}`
          }
          menuPortalTarget={document.body}
          {...searchProps}
        />
      );
    }

    // Short single-select sets (workplace, employment type) → segmented chips.
    return (
      <Flex wrap="wrap" gap="6px">
        {(field.options ?? []).map((opt) => {
          const active = values[field.key] === opt.value;
          return (
            <Box
              key={opt.value}
              as="button"
              type="button"
              onClick={() => !disabled && setValue(field.key, opt.value)}
              px="12px"
              py="7px"
              borderRadius="8px"
              border="1px solid"
              borderColor={active ? 'primary-500' : 'secondary-200'}
              bg={active ? 'primary-50' : 'base-white'}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              opacity={disabled ? 0.6 : 1}
              transition="all 0.12s"
            >
              <Text
                variant="small"
                className={
                  active ? 'text-primary-800 font-medium' : 'text-secondary-700'
                }
              >
                {opt.label}
              </Text>
            </Box>
          );
        })}
      </Flex>
    );
  };

  return (
    <Box
      data-test-id="agentforge-clarify-form-block"
      bg="base-white"
      border="1px solid"
      borderColor="primary-200"
      borderRadius="12px"
      overflow="visible"
      boxShadow="0 1px 3px rgba(16, 24, 40, 0.06)"
      maxW="520px"
    >
      <Box
        px="16px"
        py="12px"
        borderBottom="1px solid"
        borderColor="primary-100"
        bg="primary-25"
        borderTopRadius="12px"
      >
        <Text variant="span" className="text-primary-900 font-semibold">
          {data.intro}
        </Text>
      </Box>

      <Flex direction="column" gap="16px" px="16px" py="16px">
        {data.actions.map((field) => (
          <Box key={field.key}>
            <Text
              variant="span"
              className="text-secondary-700 text-sm font-medium"
            >
              {field.label}
            </Text>
            {field.help ? (
              <Text variant="small" className="text-secondary-500">
                {field.help}
              </Text>
            ) : null}
            <Box mt="4px">
              {field.type === 'picker' &&
              (field.options?.length || field.search) ? (
                renderPicker(field)
              ) : (
                <Input
                  value={(values[field.key] as string) ?? ''}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  disabled={disabled}
                  size="sm"
                />
              )}
            </Box>
          </Box>
        ))}
      </Flex>

      <Flex
        px="16px"
        py="12px"
        borderTop="1px solid"
        borderColor="secondary-100"
        bg="secondary-25"
        align="center"
        justify="space-between"
        gap="12px"
        borderBottomRadius="12px"
      >
        <Text variant="small" className="text-secondary-500">
          Review the details, then create.
        </Text>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          isDisabled={disabled}
        >
          {data.submitLabel}
        </Button>
      </Flex>
    </Box>
  );
};
